import { Link } from "@remix-run/react";
import type { ReactNode } from "react";
import React from "react";
import className from "~/kernel/styling/className";
import isNil from "~/kernel/validation/isNil";

export default function Table({ children }: { children: React.ReactNode }) {
  return (
    <div role="table" className="table w-full table-auto border-collapse">
      {children}
    </div>
  );
}

Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Td = TableData;

export function TableHead({ headings }: { headings: Array<ReactNode> }) {
  return (
    <div role="rowgroup" className="table-header-group text-left">
      <div role="row" className="table-row border-b border-b-slate-200">
        {React.Children.map(headings, (child) => (
          <div
            role="cell"
            className="first:pl-2 last:pr-2 table-cell px-5 py-3 text-left text-sm font-normal text-slate-500"
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TableBody({ children }: { children: React.ReactNode }) {
  return (
    <div role="rowgroup" className="table-row-group">
      {children}
    </div>
  );
}

export function TableRow({
  children,
  to = "",
  active = false,
  hoverable,
}: {
  children: React.ReactNode;
  to?: string;
  active?: boolean;
  hoverable?: boolean;
}) {
  const isLink = to?.length > 0;
  const isHoverable = isNil(hoverable) ? isLink : hoverable;
  const tableRowClassName = className("table-row border-t border-t-slate-200", {
    "bg-secondary text-white": active,
    "hover:bg-sky-700": active && isHoverable,
    "hover:bg-slate-100": !active && isHoverable,
  });

  if (isLink) {
    return (
      <Link role="link" className={tableRowClassName} to={to}>
        {children}
      </Link>
    );
  }

  return (
    <div role="row" className={tableRowClassName}>
      {children}
    </div>
  );
}

type TableDataProps = {
  children: ReactNode;
  padding?: "default" | "none";
  size?: "auto" | "full";
  to?: string;
};

export function TableData({
  children,
  size = "auto",
  padding = "default",
  to = "",
}: TableDataProps) {
  const isLink = to?.length > 0;

  const cellClassName = className(
    "table-cell break-all align-middle",
    {
      auto: "",
      full: "w-full",
    }[size],
  );

  const paddingClassName = {
    default: "first:pl-2 px-5 py-3 last:pr-2",
    none: "",
  }[padding];

  if (isLink) {
    return (
      <div role="cell" className={cellClassName}>
        <Link to={to} className={className("inset-0 block", paddingClassName)}>
          {children}
        </Link>
      </div>
    );
  }

  return (
    <div role="cell" className={className(cellClassName, paddingClassName)}>
      {children}
    </div>
  );
}
